import { LitSquadInventoryEntry } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowRightLeft, Hash, Package, Plus, Trash2, UserCircle } from 'lucide-react';

import {
  isLitSquadCharacter,
  isSafeImageUri,
  resolveLitSquadDisplayName,
  resolveLitSquadThumbnail,
} from '../utils';

interface InventoryItemCardProps {
  item: LitSquadInventoryEntry;
  onTransfer?: (item: LitSquadInventoryEntry) => void;
  onDelete?: (item: LitSquadInventoryEntry) => void;
  onClick?: (item: LitSquadInventoryEntry) => void;
  /** Set this character as the avatar base. Only invoked for species items. */
  onSetAvatarBase?: (item: LitSquadInventoryEntry) => void;
  /** Add this accessory on top of the in-progress avatar. Only invoked for non-species items. */
  onAddAvatarAccessory?: (item: LitSquadInventoryEntry) => void;
}

export function InventoryItemCard({
  item,
  onTransfer,
  onDelete,
  onClick,
  onSetAvatarBase,
  onAddAvatarAccessory,
}: InventoryItemCardProps) {
  const displayName = resolveLitSquadDisplayName(item);
  const thumbnailUri = resolveLitSquadThumbnail(item);
  const isCharacter = isLitSquadCharacter(item);

  return (
    <Card
      className={`overflow-hidden transition-shadow hover:shadow-md ${onClick ? 'cursor-pointer' : ''}`}
      onClick={() => onClick?.(item)}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick(item);
              }
            }
          : undefined
      }
    >
      <div className="aspect-square bg-muted flex items-center justify-center">
        {isSafeImageUri(thumbnailUri) ? (
          <img src={thumbnailUri!} alt={displayName} className="w-full h-full object-cover" />
        ) : (
          <Package className="h-12 w-12 text-muted-foreground" />
        )}
      </div>
      <CardContent className="p-3">
        <h3 className="font-medium text-sm truncate" title={displayName}>
          {displayName}
        </h3>
        <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
          <span className="truncate">{item.speciesName}</span>
          {item.instanceNumber != null && (
            <span className="flex items-center gap-0.5 ml-auto">
              <Hash className="h-3 w-3" />
              {item.instanceNumber}
            </span>
          )}
        </div>
        <div className="flex gap-1 mt-2" onClick={(e) => e.stopPropagation()}>
          {isCharacter && onSetAvatarBase && (
            <Button
              variant="secondary"
              size="sm"
              className="flex-1 h-7 text-xs"
              onClick={() => onSetAvatarBase(item)}
              aria-label={`Set ${displayName} as avatar`}
            >
              <UserCircle className="h-3 w-3 mr-1" />
              Set as avatar
            </Button>
          )}
          {!isCharacter && onAddAvatarAccessory && (
            <Button
              variant="secondary"
              size="sm"
              className="flex-1 h-7 text-xs"
              onClick={() => onAddAvatarAccessory(item)}
              aria-label={`Add ${displayName} to avatar`}
            >
              <Plus className="h-3 w-3 mr-1" />
              Add to avatar
            </Button>
          )}
          {onTransfer && (
            <Button
              variant="outline"
              size="sm"
              className="flex-1 h-7 text-xs"
              onClick={() => onTransfer(item)}
              aria-label={`Transfer ${displayName}`}
            >
              <ArrowRightLeft className="h-3 w-3 mr-1" />
              Transfer
            </Button>
          )}
          {onDelete && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs text-destructive"
              onClick={() => onDelete(item)}
              aria-label={`Burn ${displayName}`}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
